import { NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule, FormBuilder } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { TranslateModule } from '@ngx-translate/core';

import { PerfectScrollbarModule } from 'ngx-perfect-scrollbar';
import { LightboxModule } from 'ngx-lightbox';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { ChartsModule } from 'ng2-charts';
import { Angular2AirDatepickerModule } from 'angular2-air-datepicker';

import { BitfFormBuilderService } from '@bitf/core/form';
import { BitfFormControlTranslatePipe } from '@bitf/pipes/translate/bitf-form-control-translate.pipe';

import { BitfTouchEventDirective } from '@bitf/directives/bitf-touch-events/bitf-touch-event.directive';
import { BitfUiRoleManagerDirective } from '@bitf/directives/ui-role-manager/bitf-ui-role-manager.directive';
import { BitfClickOutsideDirective } from '@bitf/directives/bitf-click-outside.directive';
import { BitfClipboardDirective } from '@bitf/directives/bitf-clipbard.directive';
import { BitfDndFileComponent } from '@bitf/core/components/ui/dnd-file/bitf-dnd-file.component';

import {
  HeroValidationMessageDirective,
  HeroLoaderDirective,
  LoaderComponent,
  HeroNotEmptyDirective,
} from './directives';
import { HeroValidationMessageComponent } from './directives/validation-message/validation-message.component';

import { TruncatePipe } from './pipes/truncate/truncate.pipe';
import { HeroDatePipe } from './pipes/hero-date/hero-date.pipe';
import { HeroNumberPipe } from './pipes/number/number.pipe';
import { UserInitialsPipe } from './pipes/user-initials/user-initials.pipe';

import { MATERIAL_MODULES } from './material-modules';
import { NG_BOOTSTRAP_MODULES } from './ng-bootstrap-modules';

import { MODALS } from './components/modals';

import {
  SearchComponent,
  HeaderComponent,
  AccountCardComponent,
  CampaignCardComponent,
  ChipsComponent,
  CommonCellsTemplatesComponent,
  RadioBoxComponent,
  PreviewUploadFileComponent,
  DropdownComponent,
  CharsCounterComponent,
  InputWithChipsComponent,
  FilesUploaderComponent,
  PreviewWebsiteMetaComponent,
  DataTableComponent,
  DataTablePageSizeComponent,
  SidebarComponent,
  SidebarMenuComponent,
  SearchBarComponent,
  ChartComponent,
  SummaryCardComponent,
  DateRangeFilterComponent,
  TimeUnitSelectorComponent,
  DatepickerComponent,
  ConnectionCardComponent,
  GoogleAnalyticsCardComponent,
  InformationCardComponent,
  PanelLegendsComponent,
  PanelTabsComponent,
  InsightIconsComponent,
  ToggleButtonComponent,
  ChannelSwitchComponent,
  TableMenuComponent,
  TextWithCopyComponent,
  TextWithLinkComponent,
  PaginationComponent,
  FiltersChipsComponent,
} from './components';

import {
  RiCloseLine,
  RiCloseFill,
  RiArticleLine,
  RiArrowRightLine,
  RemixIconModule,
  RiArrowDownSLine,
  RiArrowUpSLine,
  RiAddCircleFill,
  RiCloseCircleFill,
  RiSearchLine,
  RiCheckLine,
  RiSearch2Line,
  RiLogoutCircleRLine,
  RiNotificationLine,
  RiAddLine,
  RiDonutChartFill,
  RiBillLine,
  RiSettingsLine,
  RiArrowLeftSLine,
  RiArrowLeftLine,
  RiStackLine,
  RiVipCrownLine,
  RiGitBranchFill,
  RiUserSettingsLine,
  RiLinkUnlink,
  RiCalendarLine,
  RiEditBoxLine,
  RiLockLine,
  RiGalleryLine,
  RiMoreFill,
  RiDeleteBin5Line,
  RiFileCopyLine,
  RiExternalLinkLine,
  RiSubtractLine,
  RiRefreshLine,
} from 'angular-remix-icon';

import { OkCancelDialogComponent } from './components/modals/ok-cancel-dialog/ok-cancel-dialog.component';

const SHARED_MODULES = [
  CommonModule,
  ReactiveFormsModule,
  FormsModule,
  TranslateModule,
  RouterModule,
  PerfectScrollbarModule,
  LightboxModule,
  NgxDatatableModule,
  ChartsModule,
  Angular2AirDatepickerModule,
  MATERIAL_MODULES,
  NG_BOOTSTRAP_MODULES,
];
const SHARED_COMPONENTS = [
  HeroValidationMessageComponent,
  BitfDndFileComponent,
  SearchComponent,
  HeaderComponent,
  AccountCardComponent,
  CampaignCardComponent,
  ChipsComponent,
  CommonCellsTemplatesComponent,
  RadioBoxComponent,
  PreviewUploadFileComponent,
  DropdownComponent,
  CharsCounterComponent,
  InputWithChipsComponent,
  FilesUploaderComponent,
  PreviewWebsiteMetaComponent,
  DataTableComponent,
  DataTablePageSizeComponent,
  SidebarComponent,
  SidebarMenuComponent,
  SearchBarComponent,
  ChartComponent,
  SummaryCardComponent,
  DateRangeFilterComponent,
  TimeUnitSelectorComponent,
  DatepickerComponent,
  ConnectionCardComponent,
  GoogleAnalyticsCardComponent,
  InformationCardComponent,
  PanelLegendsComponent,
  PanelTabsComponent,
  InsightIconsComponent,
  ToggleButtonComponent,
  ChannelSwitchComponent,
  TableMenuComponent,
  TextWithCopyComponent,
  TextWithLinkComponent,
  PaginationComponent,
  OkCancelDialogComponent,
  FiltersChipsComponent,
  LoaderComponent,
];
const SHARED_DIRECTIVES = [
  HeroValidationMessageDirective,
  BitfUiRoleManagerDirective,
  BitfTouchEventDirective,
  BitfClickOutsideDirective,
  BitfClipboardDirective,
  HeroLoaderDirective,
  HeroNotEmptyDirective,
];
const SHARED_BITF_COMPONENTS = [];
const SHARED_PIPES = [
  BitfFormControlTranslatePipe,
  HeroDatePipe,
  TruncatePipe,
  UserInitialsPipe,
  HeroNumberPipe,
];
const PROVIDERS = [[{ provide: FormBuilder, useClass: BitfFormBuilderService }]];

const REMIX_ICONS = {
  RiAddCircleFill,
  RiCloseCircleFill,
  RiCloseLine,
  RiCloseFill,
  RiArticleLine,
  RiArrowRightLine,
  RiArrowDownSLine,
  RiArrowUpSLine,
  RiSearchLine,
  RiCheckLine,
  RiSearch2Line,
  RiLogoutCircleRLine,
  RiNotificationLine,
  RiAddLine,
  RiDonutChartFill,
  RiBillLine,
  RiSettingsLine,
  RiArrowLeftSLine,
  RiArrowLeftLine,
  RiStackLine,
  RiVipCrownLine,
  RiGitBranchFill,
  RiUserSettingsLine,
  RiLinkUnlink,
  RiCalendarLine,
  RiEditBoxLine,
  RiLockLine,
  RiGalleryLine,
  RiMoreFill,
  RiDeleteBin5Line,
  RiFileCopyLine,
  RiExternalLinkLine,
  RiSubtractLine,
  RiRefreshLine,
};

@NgModule({
  imports: [RemixIconModule.configure(REMIX_ICONS), ...SHARED_MODULES],
  exports: [
    RemixIconModule,
    ...SHARED_MODULES,
    ...SHARED_COMPONENTS,
    ...SHARED_DIRECTIVES,
    ...SHARED_BITF_COMPONENTS,
    ...SHARED_PIPES,
  ],
  declarations: [
    ...SHARED_COMPONENTS,
    ...SHARED_DIRECTIVES,
    ...SHARED_BITF_COMPONENTS,
    ...SHARED_PIPES,
    ...MODALS,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [...PROVIDERS],
})
export class CommonSharedModule {
  // This should only be called inside the CoreModule!
}
